import type { CaosOrderedPicker } from '@caos/ui';
import type { Navigate } from './setup-surface.js';

/**
 * CAOS-732 — the Setup app's SEAM ORDER editor (Setup → Platform → Edge Tab Order).
 *
 * `orderSeamContributions` (kernel `capabilities/seam-order.ts`) already sorts a seam's
 * contributions and already accepts an admin's pinned arrangement; `CapabilityRegistry.
 * injectionsFor` already resolves and applies an org's persisted pin (`caos.seam_order_override`,
 * migration 0080) for every kernel-mastered seam. This is the first — and, this ticket, only —
 * admin-facing EDITOR for one: the right-edge gutter (`edge-anchored`), the most user-visible of
 * them (the AI assistant / Report-a-Bug / Guidance Center tab stack every
 * signed-in user sees). It is a SECTION of the existing Setup console (AC6 — "not its own
 * navigation entry"), reached at `/setup/seam-order`, the same way Theme & Branding is a section of
 * Company rather than a destination of its own.
 *
 * Scope note (CAOS-732 AC6's own instruction: "pick the most user-visible one … if building all 4
 * is too large for one ticket"): the storage + read-path wiring (`CapabilityRegistry.injectionsFor`)
 * needed no per-seam work to begin with. It groups `capability_injection` rows by whatever
 * `injection_point` they carry and applies that org's pin to each group, so an org could already
 * GET/SET a pin for ANY seam through the `seam-order` API route — the four named on this list at
 * the time (`avatar-menu`, `header-actions`, `navigation-entries`, `edge-anchored`) and any seam
 * added after. Only the EDITOR was scoped to `edge-anchored` here; editors for the
 * others, plus Setup's own `setup-nav` seam (which bypasses the registry entirely — a separate,
 * pre-existing architecture gap CAOS-431/730 already named, not something this ticket's storage
 * mechanism alone can close), are the named CAOS-732 follow-up.
 *
 * Reordering UI: the component library's ordered picker (CAOS-597) — pinned entries in order in one
 * list, the rest in the other (no drag-and-drop dependency, like every other Setup editor). AC7 — a pinned key with no CURRENTLY active claim on the seam
 * (the package was uninstalled, or never installed on this org) is rendered as a flagged, prunable
 * "stale pin" row rather than silently dropped or silently kept as invisible dead state; the route
 * itself (`seam-order/get`) computes which pins are stale, so the editor never re-derives that logic.
 *
 * CAOS-736 — that follow-up: this section is now GENERIC over the kernel-mastered shell seams
 * ({@link SEAM_ORDER_SURFACES}), not edge-anchored alone. Nothing about the fetch/paint
 * logic below was seam-specific to begin with — `SEAM_ID`/`SEAM_LABEL` were the only two constants
 * naming edge-anchored — so this stays the SAME editor, parameterized, rather than a fork per seam.
 * `setup-surface.ts` wires every entry of that list into the console (a hub page + one route per
 * seam, five of them today); Setup's own
 * `setup-nav` and the app switcher are NOT here — see `metadata-source.ts`'s `SETUP_NAV_SEAM_ID` /
 * `APP_SWITCHER_SEAM_ID` header for why those two route through a different mechanism entirely.
 */

/** One kernel-mastered shell seam this editor can render, and the copy specific to it. */
export interface SeamOrderSurfaceConfig {
  /** The `injection_point` / `seam_id` value — what `seam-order/get|set` and the pin itself key on. */
  readonly seamId: string;
  /** What this seam's stacked surfaces are called, in a sentence ("the right-edge gutter tabs"). */
  readonly label: string;
  /** The Setup rail entry + record-header heading this seam's editor carries. */
  readonly heading: string;
  /** One clause naming concrete examples of what stacks here, for the intro sentence. */
  readonly examples: string;
}

/**
 * CAOS-736 — the kernel-mastered shell seams this editor is generic over. `injectionsFor` resolves
 * an org's pin for every seam it buckets, so the constraint on this list is not what CAN be pinned
 * but what an administrator has a reason to ARRANGE: a seam that stacks visible contributions in an
 * order a person reads. That is why `profile-preferences` is absent — its contributions are settings
 * in a list, not a stack anyone reads an order into — and why CAOS-768's `home-tabs` is present.
 * Order here is display order in the Setup hub, not a ranking of the seams themselves.
 */
export const SEAM_ORDER_SURFACES: readonly SeamOrderSurfaceConfig[] = [
  {
    seamId: 'edge-anchored',
    label: 'right-edge gutter tabs',
    heading: 'Edge Tab Order',
    examples: 'the assistant, Report a Bug, Guidance Center, and any other package that anchors here',
  },
  {
    seamId: 'navigation-entries',
    label: 'top navigation entries',
    heading: 'Navigation Order',
    examples: 'the app-scoped entries a package adds to the top navigation strip',
  },
  {
    seamId: 'header-actions',
    label: 'header action buttons',
    heading: 'Header Actions Order',
    examples: 'the buttons a package adds to the workspace header',
  },
  {
    seamId: 'avatar-menu',
    label: 'avatar menu items',
    heading: 'Avatar Menu Order',
    examples: 'the entries a package adds to the account/avatar menu',
  },
  {
    // CAOS-768 — the one seam here with a FIXED CAPACITY, which is why an editor for it does more
    // than arrange: the workspace tab row draws only the first two, so the pinned order decides
    // which packages get a tab at all when more than two ask. Everything else on this list is
    // unbounded and pinning it only changes what comes first.
    seamId: 'home-tabs',
    label: 'pinned workspace tabs',
    heading: 'Pinned Tab Order',
    examples: 'the destinations a package pins into the workspace tab row, of which the first two are drawn',
  },
];

const DEFAULT_SEAM_ID = 'edge-anchored';

/** Look up a configured seam's display copy; an unknown id falls back to the edge-anchored copy. */
function configFor(seamId: string): SeamOrderSurfaceConfig {
  return SEAM_ORDER_SURFACES.find((s) => s.seamId === seamId) ?? SEAM_ORDER_SURFACES[0]!;
}

// ── The fetch seam (production → the host `/api/*` proxy; tests override it) ────────────────────

export interface MetaResponse {
  json(): Promise<unknown>;
}
export type MetaFetch = (
  path: string,
  init?: { method?: string; headers?: Record<string, string>; body?: string },
) => Promise<MetaResponse>;

export const defaultSeamOrderFetch: MetaFetch = (path, init) =>
  fetch(path, { credentials: 'same-origin', ...(init ?? {}) }) as unknown as Promise<MetaResponse>;

let metaFetch: MetaFetch = defaultSeamOrderFetch;

/** Override the fetch seam (production wiring / deterministic tests). */
export function setSeamOrderFetch(fn: MetaFetch): void {
  metaFetch = fn;
}

interface Envelope {
  ok?: boolean;
  data?: unknown;
  error?: { message?: string } | undefined;
}

function errorMessage(env: Envelope | null): string {
  const m = env?.error?.message;
  return typeof m === 'string' && m.length > 0 ? m : 'That change could not be saved. Please try again.';
}

interface SeamOrderState {
  readonly seamId: string;
  readonly sourceKeys: readonly string[];
  readonly activeSourceKeys: readonly string[];
  readonly stalePins: readonly string[];
}

/** Read the seam's current pin + active claims. `null` on any failure — the pane renders a real error. */
async function fetchSeamOrder(seamId: string): Promise<SeamOrderState | null> {
  try {
    const res = await metaFetch(`/api/seam-order/get?seamId=${encodeURIComponent(seamId)}`, { method: 'GET' });
    const env = (await res.json()) as Envelope;
    if (env?.ok !== true) return null;
    const data = env.data as Partial<SeamOrderState> | undefined;
    return {
      seamId,
      sourceKeys: Array.isArray(data?.sourceKeys) ? data.sourceKeys : [],
      activeSourceKeys: Array.isArray(data?.activeSourceKeys) ? data.activeSourceKeys : [],
      stalePins: Array.isArray(data?.stalePins) ? data.stalePins : [],
    };
  } catch {
    return null;
  }
}

interface WriteResult {
  ok: boolean;
  message?: string;
  sourceKeys?: readonly string[];
}

/** Set (non-empty) or clear (empty) the seam's pin. */
async function saveSeamOrder(seamId: string, sourceKeys: readonly string[]): Promise<WriteResult> {
  let env: Envelope;
  try {
    const res = await metaFetch('/api/seam-order/set', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ seamId, sourceKeys }),
    });
    env = (await res.json()) as Envelope;
  } catch {
    return { ok: false, message: 'The change could not be saved — the server could not be reached.' };
  }
  if (env?.ok === true) {
    const data = env.data as { sourceKeys?: unknown } | undefined;
    return { ok: true, sourceKeys: Array.isArray(data?.sourceKeys) ? (data.sourceKeys as string[]) : sourceKeys };
  }
  return { ok: false, message: errorMessage(env) };
}

// ── Small DOM helpers (mirror setup-domains.ts) ─────────────────────────────────────────────────

function button(label: string, opts: { variant?: string } = {}): HTMLElement {
  const b = document.createElement('caos-button');
  if (opts.variant) b.setAttribute('variant', opts.variant);
  b.setAttribute('size', 'sm');
  b.setAttribute('type', 'button');
  b.textContent = label;
  return b;
}

function errorBanner(message: string): HTMLElement {
  const el = document.createElement('div');
  el.className = 'seam-order-error';
  el.setAttribute('role', 'alert');
  el.textContent = message;
  return el;
}

function statusBanner(message: string): HTMLElement {
  const el = document.createElement('div');
  el.className = 'seam-order-status';
  el.setAttribute('role', 'status');
  el.textContent = message;
  return el;
}

function humanize(key: string): string {
  return key
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

/** The mark a pinned key carries once nothing claims the seam under it (AC7). */
const STALE_NOTE = 'No longer contributing — safe to remove';

export interface SeamOrderContext {
  readonly navigate?: Navigate;
  /** Which of {@link SEAM_ORDER_SURFACES} to render; defaults to the edge gutter (CAOS-732's original). */
  readonly seamId?: string;
}

/**
 * Render the Seam Order editor for one kernel-mastered shell seam (CAOS-736 — any of
 * {@link SEAM_ORDER_SURFACES}; defaults to the edge gutter, CAOS-732's original scope). Paints a
 * loading pane immediately, then fetches the seam's current pin + active claims and paints the
 * working editor. Returns `{ root, done }` (the same shape Theme & Branding / Value Sets use) so a
 * test can await the initial read.
 */
export function renderSeamOrder(ctx: SeamOrderContext = {}): { root: HTMLElement; done: Promise<void> } {
  const config = configFor(ctx.seamId ?? DEFAULT_SEAM_ID);
  const root = document.createElement('div');
  root.className = 'setup-pane seam-order';
  const loading = document.createElement('caos-empty-state');
  loading.setAttribute('title', 'Loading…');
  root.append(loading);

  const done = (async (): Promise<void> => {
    const state = await fetchSeamOrder(config.seamId);
    if (!state) {
      root.replaceChildren();
      const head = document.createElement('caos-record-header');
      head.setAttribute('object-label', 'Setup · Platform');
      head.setAttribute('heading', config.heading);
      head.setAttribute('no-icon', '');
      root.append(head);
      root.append(errorBanner('Could not load the current arrangement. Please try again.'));
      return;
    }
    paint(root, state, config);
  })();

  return { root, done };
}

function paint(root: HTMLElement, state: SeamOrderState, config: SeamOrderSurfaceConfig): void {
  root.replaceChildren();

  const head = document.createElement('caos-record-header');
  head.setAttribute('object-label', 'Setup · Platform');
  head.setAttribute('heading', config.heading);
  head.setAttribute('no-icon', '');
  root.append(head);

  const intro = document.createElement('p');
  intro.className = 'seam-order-intro';
  intro.textContent =
    `Pin the stacking order of the ${config.label} (${config.examples}). A pinned entry wins the ` +
    'position you place it in; anything you leave unpinned keeps its normal declared order, ' +
    'appended after the pins.';
  root.append(intro);

  const banners = document.createElement('div');
  banners.className = 'seam-order-banners';
  root.append(banners);

  // Working draft: the pinned order, editable. Starts from the saved pin.
  let pinned: string[] = [...state.sourceKeys];

  // CAOS-597 — choosing what to pin and putting it in order is the component library's ordered picker,
  // not rows assembled here. The pinned keys are its chosen list, in order; every key actively claiming
  // the seam and not pinned is in its available list. AC7: a pinned key with no current claim stays
  // among the options only while it is pinned, marked as safe to remove, so it is taken out
  // deliberately and never silently dropped or silently kept.
  const empty = document.createElement('p');
  empty.className = 'seam-order-empty';
  empty.textContent = 'Nothing pinned yet — this seam renders in its normal declared order.';
  root.append(empty);

  const picker = document.createElement('caos-ordered-picker') as CaosOrderedPicker;
  picker.className = 'seam-order-picker';
  picker.setAttribute('available-label', 'Not pinned — falls back to normal declared order');
  picker.setAttribute('chosen-label', 'Pinned, in this order');
  root.append(picker);

  const actions = document.createElement('div');
  actions.className = 'seam-order-actions';
  const saveBtn = button('Save order', { variant: 'primary' });
  const clearBtn = button('Clear pin (use default order)', { variant: 'ghost' });
  actions.append(saveBtn, clearBtn);
  root.append(actions);

  const setBusy = (busy: boolean): void => {
    saveBtn.toggleAttribute('disabled', busy);
    clearBtn.toggleAttribute('disabled', busy);
  };

  // Hands the picker the draft. A save never changes which keys actively claim the seam (installed
  // packages decide that, not this pin), so the stale marks are always read against the same list.
  // Only what changed is handed over: a move the picker itself reported already holds the new value,
  // and the options change only when a stale pin comes or goes.
  let shownOptions = '';
  const showDraft = (): void => {
    const active = new Set(state.activeSourceKeys);
    const options = [
      ...state.activeSourceKeys.map((key) => ({ value: key, label: humanize(key) })),
      ...pinned.filter((key) => !active.has(key)).map((key) => ({ value: key, label: humanize(key), qualifier: STALE_NOTE })),
    ];
    const optionsKey = JSON.stringify(options);
    if (optionsKey !== shownOptions) {
      shownOptions = optionsKey;
      picker.options = options;
    }
    if (JSON.stringify(picker.value) !== JSON.stringify(pinned)) picker.value = pinned;
    empty.hidden = pinned.length > 0;
  };

  picker.addEventListener('change', () => {
    pinned = picker.value;
    showDraft();
  });
  showDraft();

  saveBtn.addEventListener('click', () => {
    void (async () => {
      setBusy(true);
      banners.replaceChildren();
      const result = await saveSeamOrder(config.seamId, pinned);
      setBusy(false);
      if (!result.ok) {
        banners.append(errorBanner(result.message ?? 'That change could not be saved.'));
        return;
      }
      pinned = [...(result.sourceKeys ?? pinned)];
      showDraft();
      banners.append(statusBanner('Saved. Every user in this org sees the new order now.'));
    })();
  });

  clearBtn.addEventListener('click', () => {
    void (async () => {
      setBusy(true);
      banners.replaceChildren();
      const result = await saveSeamOrder(config.seamId, []);
      setBusy(false);
      if (!result.ok) {
        banners.append(errorBanner(result.message ?? 'The pin could not be cleared.'));
        return;
      }
      pinned = [];
      showDraft();
      banners.append(statusBanner('Cleared. This seam now uses its normal declared order.'));
    })();
  });
}
